import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import SearchInput from "@/components/SearchInput";
import { trpc } from "@/lib/trpc";
import { Link, useLocation } from "wouter";
import { User, Menu, X, Bell, ChevronDown, Search } from "lucide-react";
import { Suspense, lazy, useState } from "react";

// The authenticated account dropdown pulls in @radix-ui/react-menu (~30KB).
// It only ever renders for logged-in users, so lazy-load it: anonymous
// visitors (the majority, arriving on `/` from ads) never download radix-menu,
// and logged-in users load it on mount — past first paint, so the cost is hidden.
const NavbarAccountMenu = lazy(() => import("@/components/NavbarAccountMenu"));

/** Avatar-shaped placeholder shown while the account menu chunk loads.
 *  Matches the real trigger's footprint so there's no layout shift. */
function AccountMenuFallback() {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="gap-1.5 text-muted-foreground rounded-2xl ml-1"
      disabled
      aria-label="Account menu loading"
    >
      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
        <User className="h-3.5 w-3.5 text-primary" />
      </div>
      <span className="hidden sm:inline text-sm font-normal tracking-wide">
        Account
      </span>
      <ChevronDown className="h-3 w-3 opacity-50" />
    </Button>
  );
}

export default function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [, navigate] = useLocation();
  const [location] = useLocation();
  const notifications = trpc.notification.list.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000, // 1 min, avoid refetching on every route change
    refetchOnWindowFocus: false,
  });
  const unreadCount = notifications.data?.filter(n => !n.read).length || 0;

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border/20 shadow-sm">
      <div className="container flex items-center justify-between h-16">
        {/* Logo */}
        <Link href="/" className="flex items-center no-underline">
          <img
            src="/voom-logo.png"
            alt="Voom"
            width="80"
            height="40"
            fetchPriority="high"
            className="h-10 w-auto"
          />
        </Link>

        {/* Desktop Nav */}
        <nav
          className="hidden md:flex items-center gap-1"
          aria-label="Main navigation"
        >
          <NavLink href="/products" active={location.startsWith("/products")}>
            Browse Parts
          </NavLink>
          <NavLink href="/vendors" active={location.startsWith("/vendors")}>
            Vendors
          </NavLink>
          <NavLink
            href="/categories"
            active={location.startsWith("/categories")}
          >
            Categories
          </NavLink>
          <NavLink href="/pricing" active={location === "/pricing"}>
            Pricing
          </NavLink>
        </nav>

        {/* Desktop autocomplete search, hidden on mobile */}
        <div className="hidden md:block flex-1 max-w-sm mx-3">
          <SearchInput
            variant="compact"
            placeholder="Search parts..."
            testId="navbar-search"
          />
        </div>

        {/* Right Actions */}
        <div className="flex items-center gap-1.5">
          <Link href="/products">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground rounded-2xl min-w-[44px] min-h-[44px]"
              aria-label="Search parts"
            >
              <Search className="h-[18px] w-[18px]" />
            </Button>
          </Link>

          {isAuthenticated ? (
            <>
              {/* Sell on Voom CTA — visible to logged-in buyers (non-vendor/admin)
                  so they can self-onboard as vendors. Hidden for vendor + admin
                  roles since they already have one. */}
              {user?.role !== "vendor" && user?.role !== "admin" && (
                <Button
                  size="sm"
                  className="rounded-full shadow-[0_4px_16px_-4px_rgba(0,0,0,0.10)] text-xs sm:text-sm px-3 sm:px-4 hidden sm:inline-flex"
                  asChild
                >
                  <Link
                    href="/vendor/register"
                    className="text-white no-underline"
                  >
                    Sell on Voom
                  </Link>
                </Button>
              )}

              {/* Notifications */}
              <Link href="/notifications">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-muted-foreground rounded-2xl min-w-[44px] min-h-[44px]"
                  aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
                >
                  <Bell className="h-[18px] w-[18px]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary/90 text-[10px] font-semibold text-white flex items-center justify-center shadow-sm">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </Link>

              {/* User Menu — lazy-loaded so @radix-ui/react-menu stays out of
                  the critical chunk for anonymous visitors. */}
              <Suspense fallback={<AccountMenuFallback />}>
                <NavbarAccountMenu user={user} logout={logout} />
              </Suspense>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="rounded-full hidden sm:inline-flex"
                asChild
              >
                <Link href="/sign-in" className="no-underline">
                  Sign In
                </Link>
              </Button>
              <Button
                size="sm"
                className="rounded-full shadow-[0_4px_16px_-4px_rgba(0,0,0,0.10)] text-xs sm:text-sm px-3 sm:px-4"
                asChild
              >
                <Link
                  href="/vendor/register"
                  className="text-white no-underline"
                >
                  Sell on Voom
                </Link>
              </Button>
            </div>
          )}

          {/* Mobile Menu Toggle, only on small screens */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-muted-foreground rounded-2xl ml-0.5 min-w-[44px] min-h-[44px]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="md:hidden border-t border-white/15 glass-strong px-4 pb-5 pt-3 space-y-3"
        >
          {/* Expanded autocomplete search at the top of the mobile menu */}
          <SearchInput
            variant="compact"
            placeholder="Search parts..."
            testId="mobile-menu-search"
            onSubmit={q => {
              setMobileOpen(false);
              navigate(`/products?search=${encodeURIComponent(q)}`);
            }}
          />

          <MobileNavLink href="/products" onClick={() => setMobileOpen(false)}>
            Browse Parts
          </MobileNavLink>
          <MobileNavLink href="/vendors" onClick={() => setMobileOpen(false)}>
            Vendors
          </MobileNavLink>
          <MobileNavLink
            href="/categories"
            onClick={() => setMobileOpen(false)}
          >
            Categories
          </MobileNavLink>
          {isAuthenticated ? (
            <>
              <MobileNavLink
                href="/orders"
                onClick={() => setMobileOpen(false)}
              >
                My Orders
              </MobileNavLink>
              <MobileNavLink
                href="/requests"
                onClick={() => setMobileOpen(false)}
              >
                Request a Part
              </MobileNavLink>
              <MobileNavLink
                href="/wishlist"
                onClick={() => setMobileOpen(false)}
              >
                My Wishlist
              </MobileNavLink>
              {(user?.role === "vendor" || user?.role === "admin") && (
                <MobileNavLink
                  href="/vendor/dashboard"
                  onClick={() => setMobileOpen(false)}
                >
                  Vendor Dashboard
                </MobileNavLink>
              )}
              {user?.role === "admin" && (
                <MobileNavLink
                  href="/admin"
                  onClick={() => setMobileOpen(false)}
                >
                  Admin Panel
                </MobileNavLink>
              )}
            </>
          ) : (
            <div className="pt-2 border-t border-white/10 mt-2 space-y-1">
              <Link
                href="/sign-in"
                className="block px-4 py-3 rounded-2xl text-sm tracking-wide text-primary font-medium no-underline hover:bg-primary/5 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Sign In
              </Link>
              <Link
                href="/vendor/register"
                className="block px-4 py-3 rounded-2xl text-sm tracking-wide text-foreground font-medium no-underline hover:bg-primary/5 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                List Free
              </Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function NavLink({
  href,
  active,
  children,
}: {
  href: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={`px-4 py-2 rounded-full text-sm tracking-wide transition-all duration-300 no-underline ${
        active
          ? "bg-white/50 text-foreground shadow-[0_2px_12px_-2px_rgba(0,0,0,0.04)] backdrop-blur-sm font-medium"
          : "text-muted-foreground hover:text-foreground hover:bg-white/30"
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const [location] = useLocation();
  const active = href === "/" ? location === "/" : location.startsWith(href);
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className="block px-4 py-3 rounded-2xl text-sm tracking-wide text-foreground hover:bg-white/40 no-underline transition-colors duration-200"
    >
      {children}
    </Link>
  );
}
